import React from "react";
import {
  DollarSign, TrendingUp, Briefcase, CheckCircle, Clock,
  FileText, Users, Bot, BarChart3, Bell, Search,
  Calendar, ArrowUpRight, Sparkles, ChevronRight, Star,
  Zap, Receipt, Filter, MapPin, Phone
} from "lucide-react";

// ── Inline keyframes injected once ──
const AnimStyles = () => (
  <style>{`
    @keyframes mk-draw{0%{stroke-dashoffset:var(--dash)}100%{stroke-dashoffset:0}}
    @keyframes mk-fadeUp{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}
    @keyframes mk-growY{0%{transform:scaleY(0)}100%{transform:scaleY(1)}}
    @keyframes mk-growX{0%{transform:scaleX(0)}100%{transform:scaleX(1)}}
    @keyframes mk-slideL{0%{opacity:0;transform:translateX(-12px)}100%{opacity:1;transform:translateX(0)}}
    @keyframes mk-blink{0%,100%{opacity:1}50%{opacity:0}}
    @keyframes mk-glow{0%,100%{box-shadow:0 0 8px rgba(139,92,246,0.15)}50%{box-shadow:0 0 20px rgba(139,92,246,0.35)}}
    @keyframes mk-fillW{0%{width:0}100%{width:var(--tw)}}
    @keyframes mk-donut{0%{stroke-dashoffset:var(--dash)}100%{stroke-dashoffset:0}}
    @keyframes mk-popIn{0%{opacity:0;transform:scale(0.85)}100%{opacity:1;transform:scale(1)}}
    .mk-card{animation:mk-fadeUp .6s ease both}
    .mk-slide{animation:mk-slideL .5s ease both}
    .mk-pop{animation:mk-popIn .5s ease both}
  `}</style>
);

// ── Shared mockup chrome (title bar dots) ──
const MockupChrome = ({ children, className = "" }) => (
  <div className={`rounded-xl overflow-hidden shadow-2xl border border-white/10 bg-[#0a0f1e] ${className}`}>
    <AnimStyles />
    <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#070b17] border-b border-white/[0.06]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-3 text-[10px] text-slate-600 font-medium">SkilledShark</span>
    </div>
    {children}
  </div>
);

// ── Animated area chart SVG ──
const MiniAreaChart = () => {
  const line = "M0 50 L25 42 L50 45 L75 30 L100 35 L125 22 L150 28 L175 15 L200 10";
  const area = line + " L200 60 L0 60Z";
  const len = 320;
  return (
    <svg viewBox="0 0 200 60" className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0CC0DF" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#0CC0DF" stopOpacity="0.02" />
        </linearGradient>
        <clipPath id="areaClip">
          <rect x="0" y="0" width="200" height="60">
            <animate attributeName="width" from="0" to="200" dur="1.8s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
          </rect>
        </clipPath>
      </defs>
      <g clipPath="url(#areaClip)">
        <path d={area} fill="url(#areaGrad)" />
        <path
          d={line}
          fill="none" stroke="#0CC0DF" strokeWidth="2" strokeLinecap="round"
          strokeDasharray={len} strokeDashoffset={len}
          style={{ "--dash": len, animation: "mk-draw 1.8s ease forwards" }}
        />
      </g>
      {/* Animated dot at the end */}
      <circle cx="200" cy="10" r="3" fill="#0CC0DF" opacity="0" style={{ animation: "mk-popIn .3s ease 1.7s forwards" }} />
    </svg>
  );
};

// ── Animated bar chart SVG ──
const MiniBarChart = ({ bars = [65, 45, 80, 55, 70, 90, 60], color = "#0CC0DF", totalWidth = 120 }) => {
  const gap = totalWidth / bars.length;
  const barW = gap * 0.7;
  return (
    <svg viewBox={`0 0 ${totalWidth} 40`} className="w-full h-10">
      {bars.map((h, i) => {
        const bh = h * 0.38;
        return (
          <rect
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            x={i * gap + (gap - barW) / 2}
            y={40 - bh}
            width={barW}
            rx="2"
            height={bh}
            fill={color}
            opacity={0.15 + (i / bars.length) * 0.85}
            style={{
              transformOrigin: `${i * gap + gap / 2}px 40px`,
              animation: `mk-growY .6s ease ${i * 0.06}s both`
            }}
          />
        );
      })}
    </svg>
  );
};

// ── Animated donut chart SVG ──
const MiniDonut = ({ segments = [40, 25, 20, 15], colors = ["#0CC0DF", "#3b82f6", "#10b981", "#6366f1"] }) => {
  let offset = 0;
  const r = 15, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      {segments.map((pct, i) => {
        const dash = (pct / 100) * c;
        const el = (
          <circle
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            cx="20" cy="20" r={r}
            fill="none"
            stroke={colors[i]}
            strokeWidth="5"
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={dash}
            strokeLinecap="round"
            transform="rotate(-90 20 20)"
            style={{
              "--dash": dash,
              animation: `mk-donut .8s ease ${0.3 + i * 0.15}s forwards`,
              // We need to set the final strokeDashoffset via the animation to the correct position
              // The actual offset for positioning is handled by adjusting the dash offset base
            }}
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
};

// ── Blinking cursor ──
const BlinkCursor = () => (
  <span className="inline-block w-px h-2.5 bg-brand-400/60 ml-0.5" style={{ animation: "mk-blink 1s step-end infinite" }} />
);


// ══════════════════════════════════════════
//  1. DASHBOARD MOCKUP
// ══════════════════════════════════════════
export const DashboardMockup = ({ className = "" }) => (
  <MockupChrome className={className}>
    <div className="p-3 space-y-3" style={{ fontSize: 0 }}>
      {/* Top stats row */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "Revenue MTD", value: "$47,280", change: "+12%", icon: DollarSign, color: "text-emerald-400", bg: "bg-emerald-500/10" },
          { label: "Profit MTD", value: "$18,450", change: "+8%", icon: TrendingUp, color: "text-brand-400", bg: "bg-brand-500/10" },
          { label: "Active Jobs", value: "23", change: "+3", icon: Briefcase, color: "text-blue-400", bg: "bg-blue-500/10" },
          { label: "Completion", value: "94%", change: "+2%", icon: CheckCircle, color: "text-violet-400", bg: "bg-violet-500/10" },
        ].map((s, i) => (
          <div key={s.label} className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: `${i * 0.08}s` }}>
            <div className="flex items-center justify-between mb-1">
              <div className={`h-5 w-5 ${s.bg} rounded flex items-center justify-center`}>
                <s.icon className={`h-2.5 w-2.5 ${s.color}`} />
              </div>
              <span className="text-[8px] text-emerald-400 font-medium">{s.change}</span>
            </div>
            <p className="text-[9px] text-slate-500 leading-none">{s.label}</p>
            <p className="text-xs font-bold text-white mt-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Chart + sidebar */}
      <div className="grid grid-cols-3 gap-2">
        {/* Revenue chart */}
        <div className="col-span-2 mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".35s" }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-semibold text-slate-300">Revenue & Profit</span>
            <span className="text-[7px] text-slate-600 bg-white/5 px-1.5 py-0.5 rounded">6 months</span>
          </div>
          <div className="h-16 w-full"><MiniAreaChart /></div>
          <div className="flex gap-3 mt-1">
            <div className="flex items-center gap-1"><div className="w-2 h-1 rounded bg-brand-500" /><span className="text-[7px] text-slate-500">Revenue</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-1 rounded bg-emerald-500" /><span className="text-[7px] text-slate-500">Profit</span></div>
          </div>
        </div>

        {/* Notifications */}
        <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".4s" }}>
          <div className="flex items-center gap-1 mb-1.5">
            <Bell className="h-2.5 w-2.5 text-brand-400" />
            <span className="text-[9px] font-semibold text-slate-300">Activity</span>
          </div>
          <div className="space-y-1.5">
            {[
              { text: "Quote approved — Johnson", color: "text-emerald-400" },
              { text: "Invoice paid — $2,400", color: "text-blue-400" },
              { text: "New job scheduled", color: "text-brand-400" },
              { text: "Contract signed", color: "text-violet-400" },
            ].map((n, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="mk-slide flex items-start gap-1" style={{ animationDelay: `${0.5 + i * 0.1}s` }}>
                <div className={`w-1 h-1 rounded-full mt-1 ${n.color.replace("text-", "bg-")}`} />
                <span className="text-[7px] text-slate-400 leading-tight">{n.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's schedule */}
      <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".5s" }}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-semibold text-slate-300">Today's Schedule</span>
          <span className="text-[7px] text-brand-400">5 jobs</span>
        </div>
        <div className="space-y-1">
          {[
            { time: "8:00 AM", name: "Main St Kitchen Remodel", tech: "Mike R.", status: "In Progress", sColor: "bg-amber-500" },
            { time: "10:30 AM", name: "Johnson Residence — HVAC", tech: "Carlos D.", status: "Scheduled", sColor: "bg-blue-500" },
            { time: "1:00 PM", name: "Oak Ave Electrical Panel", tech: "Mike R.", status: "Scheduled", sColor: "bg-blue-500" },
          ].map((j, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="mk-slide flex items-center gap-2 py-1 border-b border-white/[0.04] last:border-0" style={{ animationDelay: `${0.6 + i * 0.1}s` }}>
              <span className="text-[7px] text-slate-600 w-10 shrink-0 font-mono">{j.time}</span>
              <div className={`w-0.5 h-4 rounded-full ${j.sColor}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[8px] text-slate-300 truncate">{j.name}</p>
                <p className="text-[7px] text-slate-600">{j.tech}</p>
              </div>
              <span className={`text-[6px] px-1.5 py-0.5 rounded-full ${j.status === "In Progress" ? "bg-amber-500/15 text-amber-400" : "bg-blue-500/15 text-blue-400"}`}>
                {j.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </MockupChrome>
);


// ══════════════════════════════════════════
//  2. JOBS MOCKUP
// ══════════════════════════════════════════
export const JobsMockup = ({ className = "" }) => (
  <MockupChrome className={className}>
    <div className="p-3 space-y-2.5" style={{ fontSize: 0 }}>
      {/* Stats row */}
      <div className="flex gap-2">
        {[
          { label: "Total Jobs", value: "156", color: "text-white" },
          { label: "In Progress", value: "12", color: "text-amber-400" },
          { label: "Completed", value: "89", color: "text-emerald-400" },
          { label: "Pipeline Value", value: "$128K", color: "text-brand-400" },
        ].map((s, i) => (
          <div key={s.label} className="mk-card flex-1 bg-white/[0.04] rounded-lg px-2 py-1.5 border border-white/[0.06] text-center" style={{ animationDelay: `${i * 0.07}s` }}>
            <p className={`text-[10px] font-bold ${s.color}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.value}</p>
            <p className="text-[7px] text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search & filters */}
      <div className="mk-card flex gap-2" style={{ animationDelay: ".3s" }}>
        <div className="flex-1 flex items-center gap-1.5 bg-white/[0.04] rounded-md px-2 py-1 border border-white/[0.06]">
          <Search className="h-2.5 w-2.5 text-slate-600" />
          <span className="text-[8px] text-slate-600">Search jobs...</span>
          <BlinkCursor />
        </div>
        <div className="flex gap-1">
          {["All", "Active", "Completed"].map((f, i) => (
            <div key={f} className={`px-2 py-1 rounded-md text-[7px] font-medium ${i === 0 ? "bg-brand-500/20 text-brand-400" : "bg-white/[0.04] text-slate-500"}`}>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Job cards */}
      <div className="space-y-1.5">
        {[
          { name: "Main St Kitchen Remodel", customer: "Sarah Mitchell", price: "$4,800", profit: "+34%", status: "In Progress", statusColor: "bg-amber-500", tech: "Mike R.", date: "Feb 10", progress: 65 },
          { name: "Johnson HVAC Replacement", customer: "Robert Johnson", price: "$8,200", profit: "+27%", status: "Contract Signed", statusColor: "bg-indigo-500", tech: "Carlos D.", date: "Feb 12", progress: 30 },
          { name: "Oak Ave Panel Upgrade", customer: "Linda Chen", price: "$3,500", profit: "+41%", status: "Quote Sent", statusColor: "bg-blue-500", tech: "—", date: "Feb 14", progress: 10 },
          { name: "Elm St Water Heater", customer: "James Wilson", price: "$2,100", profit: "+29%", status: "Completed", statusColor: "bg-emerald-500", tech: "Mike R.", date: "Feb 8", progress: 100 },
        ].map((j, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="mk-slide flex items-center gap-2 bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: `${0.35 + i * 0.1}s` }}>
            <div className={`w-0.5 h-10 rounded-full shrink-0 ${j.statusColor}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <p className="text-[9px] font-semibold text-white truncate">{j.name}</p>
                <span className={`text-[6px] px-1.5 py-0.5 rounded-full shrink-0 ${j.statusColor}/15 ${j.statusColor.replace("bg-", "text-")}`}>
                  {j.status}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[7px] text-slate-500">
                <span>{j.customer}</span><span>·</span><span>{j.tech}</span><span>·</span><span>{j.date}</span>
              </div>
              {/* Animated progress bar */}
              <div className="mt-1 h-0.5 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${j.statusColor}`}
                  style={{ width: `${j.progress}%`, transformOrigin: "left", animation: `mk-growX .8s ease ${0.5 + i * 0.1}s both` }}
                />
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] font-bold text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{j.price}</p>
              <p className="text-[7px] text-emerald-400">{j.profit}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </MockupChrome>
);


// ══════════════════════════════════════════
//  3. INVOICES MOCKUP
// ══════════════════════════════════════════
export const InvoicesMockup = ({ className = "" }) => (
  <MockupChrome className={className}>
    <div className="p-3 space-y-2.5" style={{ fontSize: 0 }}>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Outstanding", value: "$12,400", sub: "5 invoices", vColor: "text-brand-400" },
          { label: "Collected MTD", value: "$34,800", sub: "18 paid", vColor: "text-emerald-400" },
          { label: "Overdue", value: "$3,200", sub: "2 overdue", vColor: "text-red-400" },
        ].map((s, i) => (
          <div key={s.label} className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: `${i * 0.08}s` }}>
            <p className="text-[7px] text-slate-500">{s.label}</p>
            <p className={`text-xs font-bold ${s.vColor}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.value}</p>
            <p className="text-[7px] text-slate-600">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Animated aging bar */}
      <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".25s" }}>
        <p className="text-[8px] text-slate-400 mb-1">Aging Overview</p>
        <div className="h-2 w-full rounded-full overflow-hidden flex">
          {[
            { color: "bg-emerald-500", w: "55%" },
            { color: "bg-amber-500", w: "25%" },
            { color: "bg-brand-500", w: "12%" },
            { color: "bg-red-500", w: "8%" },
          ].map((seg, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className={`${seg.color} h-full`} style={{ width: seg.w, transformOrigin: "left", animation: `mk-growX .7s ease ${0.4 + i * 0.1}s both` }} />
          ))}
        </div>
        <div className="flex justify-between mt-1">
          {[
            { label: "0-30d", pct: "55%", color: "text-emerald-400" },
            { label: "31-60d", pct: "25%", color: "text-amber-400" },
            { label: "61-90d", pct: "12%", color: "text-brand-400" },
            { label: "90d+", pct: "8%", color: "text-red-400" },
          ].map((a) => (
            <span key={a.label} className={`text-[6px] ${a.color}`}>{a.label} ({a.pct})</span>
          ))}
        </div>
      </div>

      {/* Invoice list */}
      <div className="space-y-1">
        {[
          { num: "INV-0042", customer: "Sarah Mitchell", amount: "$4,800", status: "Paid", sColor: "bg-emerald-500", date: "Feb 8" },
          { num: "INV-0041", customer: "Robert Johnson", amount: "$8,200", status: "Sent", sColor: "bg-blue-500", date: "Feb 6" },
          { num: "INV-0040", customer: "Linda Chen", amount: "$3,500", status: "Viewed", sColor: "bg-violet-500", date: "Feb 4" },
          { num: "INV-0039", customer: "James Wilson", amount: "$2,100", status: "Overdue", sColor: "bg-red-500", date: "Jan 28" },
          { num: "INV-0038", customer: "Patricia Lee", amount: "$5,600", status: "Paid", sColor: "bg-emerald-500", date: "Jan 25" },
        ].map((inv, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="mk-slide flex items-center gap-2 bg-white/[0.03] rounded-lg px-2 py-1.5 border border-white/[0.04]" style={{ animationDelay: `${0.4 + i * 0.08}s` }}>
            <div className={`w-0.5 h-6 rounded-full ${inv.sColor}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-mono text-slate-400">{inv.num}</span>
                <span className={`text-[6px] px-1 py-0.5 rounded ${inv.sColor}/15 ${inv.sColor.replace("bg-", "text-")}`}>
                  {inv.status}
                </span>
              </div>
              <p className="text-[8px] text-slate-300">{inv.customer}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-bold text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{inv.amount}</p>
              <p className="text-[6px] text-slate-600">{inv.date}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </MockupChrome>
);


// ══════════════════════════════════════════
//  4. PRICEBOOK MOCKUP
// ══════════════════════════════════════════
export const PricebookMockup = ({ className = "" }) => (
  <MockupChrome className={className}>
    <div className="p-3 space-y-2.5" style={{ fontSize: 0 }}>
      {/* Header */}
      <div className="mk-card flex items-center justify-between" style={{ animationDelay: "0s" }}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-white">Pricebook</span>
          <span className="text-[7px] bg-brand-500/15 text-brand-400 px-1.5 py-0.5 rounded-full">42 items</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 bg-violet-500/15 text-violet-400 px-1.5 py-0.5 rounded-md">
            <Sparkles className="h-2 w-2" />
            <span className="text-[7px] font-medium">AI Advisor</span>
          </div>
          <div className="bg-brand-500/80 text-white px-1.5 py-0.5 rounded-md text-[7px] font-medium">+ Add Item</div>
        </div>
      </div>

      {/* Search */}
      <div className="mk-card flex items-center gap-1.5 bg-white/[0.04] rounded-md px-2 py-1 border border-white/[0.06]" style={{ animationDelay: ".1s" }}>
        <Search className="h-2.5 w-2.5 text-slate-600" />
        <span className="text-[8px] text-slate-600">Search pricebook items...</span>
        <BlinkCursor />
      </div>

      {/* Table header */}
      <div className="mk-card grid grid-cols-12 gap-1 px-2 py-1 text-[7px] font-semibold text-slate-500 uppercase tracking-wider border-b border-white/[0.06]" style={{ animationDelay: ".15s" }}>
        <span className="col-span-4">Item</span>
        <span className="col-span-2">Category</span>
        <span className="col-span-2 text-right">Cost</span>
        <span className="col-span-2 text-right">Price</span>
        <span className="col-span-2 text-right">Margin</span>
      </div>

      {/* Table rows */}
      <div className="space-y-0.5">
        {[
          { name: "Standard Service Call", cat: "Service", cost: "$45", price: "$95", margin: "53%", mColor: "text-emerald-400" },
          { name: "Water Heater Install", cat: "Install", cost: "$320", price: "$850", margin: "62%", mColor: "text-emerald-400" },
          { name: "Panel Upgrade 200A", cat: "Electrical", cost: "$480", price: "$1,200", margin: "60%", mColor: "text-emerald-400" },
          { name: "AC Compressor Replace", cat: "HVAC", cost: "$750", price: "$1,800", margin: "58%", mColor: "text-emerald-400" },
          { name: "Drain Cleaning", cat: "Service", cost: "$25", price: "$175", margin: "86%", mColor: "text-emerald-400" },
          { name: "Toilet Replacement", cat: "Install", cost: "$110", price: "$350", margin: "69%", mColor: "text-emerald-400" },
          { name: "Ductwork Repair", cat: "HVAC", cost: "$180", price: "$420", margin: "57%", mColor: "text-amber-400" },
          { name: "Outlet Install", cat: "Electrical", cost: "$12", price: "$125", margin: "90%", mColor: "text-emerald-400" },
        ].map((item, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={`mk-slide grid grid-cols-12 gap-1 px-2 py-1.5 rounded text-[8px] ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}
            style={{ animationDelay: `${0.2 + i * 0.06}s` }}
          >
            <span className="col-span-4 text-slate-300 truncate font-medium">{item.name}</span>
            <span className="col-span-2 text-slate-500">{item.cat}</span>
            <span className="col-span-2 text-right text-slate-400 font-mono">{item.cost}</span>
            <span className="col-span-2 text-right text-white font-mono font-semibold">{item.price}</span>
            <span className={`col-span-2 text-right font-mono font-semibold ${item.mColor}`}>{item.margin}</span>
          </div>
        ))}
      </div>

      {/* AI Advisor bar — glowing */}
      <div
        className="mk-card bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-lg p-2 border border-violet-500/20"
        style={{ animationDelay: ".7s", animation: "mk-fadeUp .6s ease .7s both, mk-glow 3s ease-in-out 1.3s infinite" }}
      >
        <div className="flex items-center gap-1.5">
          <Bot className="h-3 w-3 text-violet-400" />
          <span className="text-[8px] text-violet-300 font-medium">AI Advisor:</span>
          <span className="text-[7px] text-slate-400">Your average margin is 67% — 12% above industry benchmark.</span>
        </div>
      </div>
    </div>
  </MockupChrome>
);


// ══════════════════════════════════════════
//  5. ANALYTICS MOCKUP
// ══════════════════════════════════════════
export const AnalyticsMockup = ({ className = "" }) => (
  <MockupChrome className={className}>
    <div className="p-3 space-y-2.5" style={{ fontSize: 0 }}>
      {/* KPI row */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "Total Revenue", value: "$142,800", icon: DollarSign, color: "text-emerald-400", bg: "from-emerald-500/10 to-emerald-500/5" },
          { label: "Total Profit", value: "$52,360", icon: TrendingUp, color: "text-brand-400", bg: "from-brand-500/10 to-brand-500/5" },
          { label: "Avg Hourly Rate", value: "$94/hr", icon: Clock, color: "text-blue-400", bg: "from-blue-500/10 to-blue-500/5" },
          { label: "Profit Margin", value: "36.7%", icon: BarChart3, color: "text-violet-400", bg: "from-violet-500/10 to-violet-500/5" },
        ].map((kpi, i) => (
          <div key={kpi.label} className={`mk-card bg-gradient-to-br ${kpi.bg} rounded-lg p-2 border border-white/[0.06]`} style={{ animationDelay: `${i * 0.08}s` }}>
            <kpi.icon className={`h-3 w-3 ${kpi.color} mb-1`} />
            <p className="text-[7px] text-slate-500">{kpi.label}</p>
            <p className="text-[10px] font-bold text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-2 gap-2">
        {/* Revenue by job type — animated horizontal bars */}
        <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".35s" }}>
          <p className="text-[8px] text-slate-400 mb-1.5">Revenue by Job Type</p>
          <div className="space-y-1">
            {[
              { type: "HVAC Install", pct: 35, color: "bg-brand-500", value: "$49,980" },
              { type: "Plumbing", pct: 28, color: "bg-blue-500", value: "$39,984" },
              { type: "Electrical", pct: 22, color: "bg-amber-500", value: "$31,416" },
              { type: "Maintenance", pct: 15, color: "bg-emerald-500", value: "$21,420" },
            ].map((bar, i) => (
              <div key={bar.type}>
                <div className="flex justify-between mb-0.5">
                  <span className="text-[7px] text-slate-400">{bar.type}</span>
                  <span className="text-[7px] text-slate-500 font-mono">{bar.value}</span>
                </div>
                <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${bar.color}`}
                    style={{ width: `${bar.pct}%`, transformOrigin: "left", animation: `mk-growX .7s ease ${0.5 + i * 0.1}s both` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profit distribution — animated donut */}
        <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".4s" }}>
          <p className="text-[8px] text-slate-400 mb-1.5">Profit Distribution</p>
          <div className="flex items-center gap-3">
            <MiniDonut />
            <div className="space-y-1">
              {[
                { label: "HVAC", color: "bg-brand-500", pct: "40%" },
                { label: "Plumbing", color: "bg-blue-500", pct: "25%" },
                { label: "Electrical", color: "bg-emerald-500", pct: "20%" },
                { label: "Other", color: "bg-violet-500", pct: "15%" },
              ].map((seg, i) => (
                <div key={seg.label} className="mk-slide flex items-center gap-1" style={{ animationDelay: `${0.5 + i * 0.08}s` }}>
                  <div className={`w-1.5 h-1.5 rounded-sm ${seg.color}`} />
                  <span className="text-[7px] text-slate-400">{seg.label}</span>
                  <span className="text-[7px] text-slate-600 ml-auto font-mono">{seg.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly trend — animated bars */}
      <div className="mk-card bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]" style={{ animationDelay: ".55s" }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] text-slate-400">Monthly Revenue Trend</span>
          <span className="text-[7px] text-emerald-400 font-medium">+18% YoY</span>
        </div>
        <MiniBarChart bars={[40, 52, 48, 65, 58, 72, 68, 78, 85, 75, 88, 92]} color="#0CC0DF" totalWidth={200} />
        <div className="flex justify-between mt-0.5">
          {["Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb"].map((m) => (
            <span key={m} className="text-[5px] text-slate-700">{m}</span>
          ))}
        </div>
      </div>
    </div>
  </MockupChrome>
);
